import axios from 'axios';
import { API_URL } from '../constants';

const authHeaders = (token) => ({
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + token,
  },
});

/**
 * Récupère tous les clientLoc existants et retourne une Map id -> { client, localisation }.
 * Sert à résoudre camera.clientLoc quand il n'est pas populé (juste un ID string).
 */
export async function fetchClientLocMap(token) {
  const response = await axios.get(
    `${API_URL}/boitier/listclientloc`,
    authHeaders(token)
  );
  const list = response.data.data;
  const map = new Map();
  list.forEach((entry) => map.set(entry._id, entry));
  return map;
}

/**
 * Résout le champ clientLoc d'une caméra, qu'il soit déjà peuplé (objet)
 * ou juste un ID (string) — dans ce cas on le cherche dans clientLocMap.
 */
export function resolveClientLoc(camera, clientLocMap) {
  if (!camera.clientLoc) return null;
  if (typeof camera.clientLoc === 'object') return camera.clientLoc;
  return clientLocMap.get(camera.clientLoc) || null;
}

/** Récupère la liste des cameras de l'utilisateur, et retourne celle correspondant à cameraId (avec clientLoc résolu) */
export async function fetchCameraById(cameraId, user) {
  const [camerasResponse, clientLocMap] = await Promise.all([
    axios.get(`${API_URL}/camera/listcamera?id=${user.userId}`, authHeaders(user.token)),
    fetchClientLocMap(user.token),
  ]);
  const cameras = camerasResponse.data.data;
  const camera = cameras.find((c) => c._id === cameraId);
  if (!camera) return null;
  return { ...camera, clientLoc: resolveClientLoc(camera, clientLocMap) };
}

/** Récupère les trames caméra sur l'intervalle [d1, d2] (format 'YYYY-MM-DD'). Triées par timestamp croissant. */
export async function fetchTramesCamera(cameraId, d1, d2, token) {
  const response = await axios.get(
    `${API_URL}/tramescamera/?d1=${d1}&d2=${d2}&id=${cameraId}`,
    authHeaders(token)
  );
  return response.data.data;
}

/**
 * Extrait le nom de fichier depuis une imageUrl complète (ex: ".../images/xxx.jpg" -> "xxx.jpg")
 */
function extractFileName(imageUrl) {
  return imageUrl.split('/').pop();
}

/** Récupère une URL temporaire (présignée) pour accéder à une image stockée. */
export async function fetchPresignedImageUrl(fileName, token, expiry = 3600) {
  const response = await axios.get(
    `${API_URL}/tramescamera/image/${fileName}/presigned?expiry=${expiry}`,
    authHeaders(token)
  );
  return response.data.presignedUrl;
}

/**
 * Récupère le dernier relevé (trame) d'une caméra avec son image présignée.
 * Pas d'endpoint "dernière trame" dédié côté back, donc on récupère un intervalle
 * et on prend le dernier élément (le back trie déjà par timestamp croissant).
 * Si aucune trame n'est trouvée dans les dernières 24h, on élargit la recherche
 * d'un jour supplémentaire à chaque tentative, jusqu'à trouver une trame
 * avec une image valide (ou atteindre maxDaysBack).
 */
export async function fetchLatestCameraSnapshot(cameraId, token, maxDaysBack = 365) {
  const dayAfterTomorrow = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000);
  const d2 = dayAfterTomorrow.toISOString().slice(0, 10);

  let daysBack = 1;

  while (daysBack <= maxDaysBack) {
    const start = new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000);
    const d1 = start.toISOString().slice(0, 10);

    const trames = await fetchTramesCamera(cameraId, d1, d2, token);

    if (trames && trames.length > 0) {
      const latest = trames[trames.length - 1];
      try {
        const fileName = extractFileName(latest.imageUrl);
        const presignedUrl = await fetchPresignedImageUrl(fileName, token);
        return {
          timestamp: latest.timestamp,
          compteur: latest.compteur,
          presignedUrl,
        };
      } catch (err) {
        console.warn(`Image introuvable pour la trame du ${latest.timestamp}, élargissement de la recherche...`);
      }
    }

    daysBack += 1;
  }

  return null;
}

/** Change le nom de la camera. Réponse: objet camera directement. */
export async function updateCameraName(cameraId, newName, token) {
  const response = await axios.post(
    `${API_URL}/camera/nomcamera`,
    { id_camera: cameraId, nom_camera: newName },
    authHeaders(token)
  );
  return response.data;
}

/** Configure le seuil de dépassement (en m3). Réponse: objet camera directement. */
export async function updateDepassementThreshold(cameraId, depassementM3, token) {
  const response = await axios.put(
    `${API_URL}/camera/depassement`,
    { id_camera: cameraId, depassement: depassementM3 },
    authHeaders(token)
  );
  return response.data;
} 

/**
 * Associe/modifie le client et la localisation d'une caméra.
 * Réponse: objet camera avec clientLoc populé { _id, client, localisation }.
 */
export async function setCameraClientLoc(cameraId, client, localisation, token) {
  const response = await axios.put(
    `${API_URL}/camera/setclientloc`,
    { id_camera: cameraId, client, localisation },
    authHeaders(token)
  );
  return response.data;
}

/**
 * Retire le client/localisation d'une caméra.
 * ATTENTION: pas d'endpoint dédié côté back — contournement via setclientloc
 * avec des valeurs vides.
 */
export async function clearCameraClientLoc(cameraId, token) {
  return setCameraClientLoc(cameraId, '', '', token);
}

/** Configure la fréquence de relance des emails (en jours). */
export async function updateFrequenceRelance(cameraId, frequenceRelance, token) {
  const response = await axios.put(
    `${API_URL}/camera/relance`,
    { id_camera: cameraId, frequenceRelance },
    authHeaders(token)
  );
  return response.data;
}